/**
 * Emergent-managed Google Auth — frontend bridge.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 * The redirect URL must be derived from window.location.origin so it works
 * automatically in preview, production, and any custom domain.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiClient } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

/** Kick off Emergent Google OAuth flow. */
export function startGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/** AuthCallback page — processes #session_id=… in URL and exchanges for our JWT. */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    // Run only once even under React StrictMode.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    if (!sessionId) {
      toast.error("No recibimos sesión de Google. Intenta de nuevo.");
      navigate("/cuenta/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await apiClient.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        trackEvent("login", { method: "google", role: data?.role });
        toast.success(`¡Bienvenido${data?.nombre ? ", " + data.nombre.split(" ")[0] : ""}!`);
        // Clear the URL hash so refresh doesn't re-trigger.
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data?.role === "admin" ? "/admin" : "/cuenta", { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo iniciar sesión con Google");
        navigate("/cuenta/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }} data-testid="auth-callback">
      <i className="fas fa-spinner fa-spin" style={{ fontSize: 56, color: "#163680" }} />
      <p style={{ color: "#163680", fontWeight: 600 }}>Iniciando sesión con Google…</p>
    </div>
  );
}
